import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Switch,
  Chip,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Divider,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "@webuild-ai/react-auth/stores";
import { useRBAC } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { siteConfig } from "@/config/site";
import { SERVICES, type ServiceConfig, type IntegrationStatus } from "@/config/integrations";
import { useIntegrationStatusStore } from "@/stores/IntegrationStatusStore";
import { logger } from "@/telemetry";

import { ServiceCredentialSection } from "./ServiceCredentialSection";

interface IntegrationDefinition {
  id: string;
  display_name: string;
  category: string;
  icon: string;
  token_mode: string;
  service_operations: string[];
  service_credential_type?: string;
  service_setup_guide?: string;
  has_oauth: boolean;
}

interface UserSearchSettings {
  services: Array<{
    service_type: string;
    display_name: string;
    icon: string;
    connected: boolean;
    enabled_for_search: boolean;
    connected_email?: string;
    supports_user_search: boolean;
  }>;
}

export const IntegrationsSettings: React.FC = () => {
  const { t } = useTranslation();
  const { authenticatedFetch } = useWebuildAPIClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { recordActivity } = useSessionStore();
  const { hasPermission } = useRBAC();

  const isAdmin = hasPermission("*", "admin");

  const [serviceStatuses, setServiceStatuses] = useState<Record<string, IntegrationStatus>>({});
  const [integrationDefinitions, setIntegrationDefinitions] = useState<
    Record<string, IntegrationDefinition>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [connectingService, setConnectingService] = useState<string | null>(null);
  const [disconnectingService, setDisconnectingService] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceConfig | null>(null);
  const [userSearchSettings, setUserSearchSettings] = useState<UserSearchSettings | null>(null);
  const [savingUserSearch, setSavingUserSearch] = useState<string | null>(null);
  const [supportsAllDrives, setSupportsAllDrives] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [integrationToggles, setIntegrationToggles] = useState<Record<string, boolean>>({});
  const oauthCleanupRef = React.useRef<(() => void) | null>(null);

  const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");

  /**
   * Fetch status for all services, skipping globally disabled integrations.
   */
  const fetchAllStatuses = useCallback(
    async (disabledTypes?: Set<string>) => {
      if (!authenticatedFetch) return;

      try {
        setIsLoading(true);
        const statuses: Record<string, IntegrationStatus> = {};

        // Skip status fetch for globally disabled integrations
        const activeServices = disabledTypes
          ? SERVICES.filter((s) => !disabledTypes.has(s.id))
          : SERVICES;

        // Fetch status for each active service in parallel
        const results = await Promise.allSettled(
          activeServices.map(async (service) => {
            const response = await authenticatedFetch(`${baseUrl}${service.endpoints.status}`);

            if (response.ok) {
              const data: IntegrationStatus = await response.json();

              return { id: service.id, status: data };
            } else {
              return {
                id: service.id,
                status: { connected: false, integration_type: service.id },
              };
            }
          }),
        );

        results.forEach((result) => {
          if (result.status === "fulfilled") {
            statuses[result.value.id] = result.value.status;
          }
        });

        setServiceStatuses(statuses);

        // Populate shared cache so ToolPicker benefits from pre-warmed data
        useIntegrationStatusStore.getState().setStatuses(statuses);

        // Set Google Drive shared drives setting
        if (statuses.google_drive?.settings?.supports_all_drives !== undefined) {
          setSupportsAllDrives(statuses.google_drive.settings.supports_all_drives);
        }
      } catch (error) {
        logger.error("Error fetching service statuses", { error: String(error) });
      } finally {
        setIsLoading(false);
      }
    },
    [authenticatedFetch, baseUrl],
  );

  /**
   * Fetch integration definitions from the catalogue
   */
  const fetchIntegrationDefinitions = useCallback(async () => {
    if (!authenticatedFetch) return;

    try {
      const response = await authenticatedFetch(`${baseUrl}/admin/v1/integrations/definitions`);

      if (response.ok) {
        const data: IntegrationDefinition[] = await response.json();
        const defsMap: Record<string, IntegrationDefinition> = {};

        data.forEach((def) => {
          defsMap[def.id] = def;
        });
        setIntegrationDefinitions(defsMap);
      }
    } catch (error) {
      logger.error("Error fetching integration definitions", { error: String(error) });
    }
  }, [authenticatedFetch, baseUrl]);

  /**
   * Fetch tenant catalogue to populate initial toggle states.
   * Returns the set of disabled integration types so callers can skip them.
   */
  const fetchTenantCatalog = useCallback(async (): Promise<Set<string>> => {
    if (!authenticatedFetch) return new Set();

    try {
      const response = await authenticatedFetch(`${baseUrl}/admin/v1/integrations/tenant/catalog`);

      if (response.ok) {
        const data: Array<{ integration_type: string; enabled: boolean }> = await response.json();
        const toggles: Record<string, boolean> = {};
        const disabled = new Set<string>();

        data.forEach((item) => {
          toggles[item.integration_type] = item.enabled;
          if (!item.enabled) disabled.add(item.integration_type);
        });
        setIntegrationToggles(toggles);

        return disabled;
      }
    } catch (error) {
      logger.error("Error fetching tenant catalogue", { error: String(error) });
    }

    return new Set();
  }, [authenticatedFetch, baseUrl]);

  /**
   * Fetch user search settings
   */
  const fetchUserSearchSettings = useCallback(async () => {
    if (!authenticatedFetch) return;

    try {
      const response = await authenticatedFetch(
        `${baseUrl}/admin/v1/mentions/user-search/settings`,
      );

      if (response.ok) {
        const data: UserSearchSettings = await response.json();

        setUserSearchSettings(data);
      }
    } catch (error) {
      logger.error("Error fetching user search settings", { error: String(error) });
    }
  }, [authenticatedFetch, baseUrl]);

  useEffect(() => {
    // Fetch catalogue first so we know which integrations are disabled,
    // then skip status fetches for disabled ones (avoids pointless 404s).
    fetchTenantCatalog().then((disabled) => fetchAllStatuses(disabled));
    fetchUserSearchSettings();
    fetchIntegrationDefinitions();
  }, [fetchAllStatuses, fetchUserSearchSettings, fetchIntegrationDefinitions, fetchTenantCatalog]);

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const integration = params.get("integration");

    if (integration) {
      if (success === "true") {
        fetchAllStatuses();
        fetchUserSearchSettings();
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchAllStatuses, fetchUserSearchSettings]);

  // Scroll to anchor if present
  useEffect(() => {
    const hash = window.location.hash;

    if (hash === "#mentions") {
      const element = document.getElementById("mentions");

      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [isLoading]);

  const handleConnect = async (service: ServiceConfig) => {
    if (!authenticatedFetch) return;

    try {
      setConnectingService(service.id);
      recordActivity(false);

      const response = await authenticatedFetch(`${baseUrl}${service.endpoints.authorize}`);

      if (response.ok) {
        const data = await response.json();

        // Open OAuth in a popup window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          data.auth_url,
          `${service.id}-oauth`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
        );

        let isCleanedUp = false;
        let statusRefreshed = false;
        let pollTimer: ReturnType<typeof setInterval> | null = null;

        const cleanup = () => {
          if (isCleanedUp) return;
          isCleanedUp = true;
          if (pollTimer) clearInterval(pollTimer);
          window.removeEventListener("message", handleMessage);
          setConnectingService(null);
        };

        const refreshStatus = async () => {
          if (statusRefreshed) return;
          statusRefreshed = true;
          recordActivity(false);
          await new Promise((resolve) => setTimeout(resolve, 1500));
          recordActivity(false);

          try {
            await fetchAllStatuses();
            await fetchUserSearchSettings();
          } catch (error) {
            logger.error(`Failed to refresh ${service.name} status`, { error: String(error) });
          }
        };

        const handleMessage = (event: MessageEvent) => {
          const expectedOrigin = window.location.origin;

          if (event.origin !== expectedOrigin) return;
          if (event.source !== popup) return;

          // Handle different OAuth callback types
          if (
            event.data?.type === "google-oauth-callback" ||
            event.data?.type === "jira-oauth-callback" ||
            event.data?.type === "github-oauth-callback" ||
            event.data?.type === "oauth-callback"
          ) {
            cleanup();
            if (event.data.success) {
              refreshStatus();
            }
            try {
              popup?.close();
            } catch {
              // Popup may already be closed
            }
          }
        };

        window.addEventListener("message", handleMessage);
        oauthCleanupRef.current = cleanup;

        // Poll for popup closure so the spinner resets when the user
        // closes the popup without completing the OAuth flow.
        pollTimer = setInterval(() => {
          if (popup?.closed) {
            cleanup();
          }
        }, 500);
      } else {
        throw new Error("Failed to get authorization URL");
      }
    } catch (error) {
      logger.error(`Failed to connect ${service.name}`, { error: String(error) });
      setConnectingService(null);
    }
  };

  const handleDisconnect = async () => {
    if (!authenticatedFetch || !selectedService) return;

    try {
      setDisconnectingService(selectedService.id);
      const response = await authenticatedFetch(`${baseUrl}${selectedService.endpoints.revoke}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const disconnectedStatus: IntegrationStatus = {
          connected: false,
          integration_type: selectedService.id,
        };

        setServiceStatuses((prev) => ({
          ...prev,
          [selectedService.id]: disconnectedStatus,
        }));
        useIntegrationStatusStore.getState().setStatus(selectedService.id, disconnectedStatus);
        onClose();
        fetchUserSearchSettings();
      }
    } catch (error) {
      logger.error(`Failed to disconnect ${selectedService.name}`, { error: String(error) });
    } finally {
      setDisconnectingService(null);
    }
  };

  const handleUserSearchToggle = async (serviceType: string, enabled: boolean) => {
    if (!authenticatedFetch) return;

    try {
      setSavingUserSearch(serviceType);

      const response = await authenticatedFetch(
        `${baseUrl}/admin/v1/mentions/user-search/settings`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ service_type: serviceType, enabled }),
        },
      );

      if (response.ok) {
        // Update local state
        setUserSearchSettings((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            services: prev.services.map((s) =>
              s.service_type === serviceType ? { ...s, enabled_for_search: enabled } : s,
            ),
          };
        });
      }
    } catch (error) {
      logger.error(`Failed to update user search for ${serviceType}`, { error: String(error) });
    } finally {
      setSavingUserSearch(null);
    }
  };

  const handleSupportsAllDrivesChange = async (value: boolean) => {
    if (!authenticatedFetch || !serviceStatuses.google_drive?.connected) return;
    const gdService = SERVICES.find((s) => s.id === "google_drive");

    if (!gdService?.endpoints.settings) return;

    try {
      setIsSavingSettings(true);
      setSupportsAllDrives(value);

      const response = await authenticatedFetch(`${baseUrl}${gdService.endpoints.settings}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supports_all_drives: value }),
      });

      if (!response.ok) {
        setSupportsAllDrives(!value);
      }
    } catch (error) {
      logger.error("Failed to update Google Drive settings", { error: String(error) });
      setSupportsAllDrives(!value);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Cleanup OAuth listeners on unmount
  useEffect(() => {
    return () => {
      if (oauthCleanupRef.current) {
        oauthCleanupRef.current();
        oauthCleanupRef.current = null;
      }
    };
  }, []);

  const openDisconnectModal = (service: ServiceConfig) => {
    setSelectedService(service);
    onOpen();
  };

  const getSearchEnabledForService = (serviceId: string): boolean => {
    const setting = userSearchSettings?.services.find((s) => s.service_type === serviceId);

    return setting?.enabled_for_search ?? false;
  };

  return (
    <Card data-testid="integrations-settings" id="integrations">
      <CardHeader>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Icon className="w-6 h-6" icon="solar:link-bold" />
          <span>{t("Integrations")}</span>
        </h2>
      </CardHeader>
      <CardBody className="space-y-6">
        {/* Services List */}
        {SERVICES.filter(
          (service) =>
            (integrationToggles[service.id] ?? true) !== false && service.id !== "ms_calendar", // Rendered under Microsoft 365 SSO section
        ).map((service, index) => {
          const status = serviceStatuses[service.id];
          const isConnected = status?.connected ?? false;
          const isConnecting = connectingService === service.id;

          return (
            <React.Fragment key={service.id}>
              {index > 0 && <Divider />}
              <div className="border border-default-200 rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Icon className="w-10 h-10" icon={service.icon} />
                    <div>
                      <h3 className="font-medium">{t(service.name)}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t(service.description)}
                      </p>
                    </div>
                  </div>

                  {isLoading ? (
                    <Spinner size="sm" />
                  ) : isConnected ? (
                    <div className="flex items-center gap-3">
                      {status?.token_status === "expired" ||
                      status?.token_status === "refresh_failed" ? (
                        <>
                          <Chip
                            color="warning"
                            size="sm"
                            startContent={<Icon icon="solar:danger-triangle-bold" />}
                          >
                            {status.token_status === "refresh_failed"
                              ? t("Reconnection Required")
                              : t("Token Expired")}
                          </Chip>
                          <Button
                            color="warning"
                            data-testid={`reconnect-${service.id}`}
                            isLoading={isConnecting}
                            size="sm"
                            variant="flat"
                            onPress={() => handleConnect(service)}
                          >
                            {t("Reconnect")}
                          </Button>
                        </>
                      ) : (
                        <Chip
                          color="success"
                          size="sm"
                          startContent={<Icon icon="solar:check-circle-bold" />}
                        >
                          {t("Connected")}
                        </Chip>
                      )}
                      <Button
                        color="danger"
                        data-testid={`disconnect-${service.id}`}
                        size="sm"
                        variant="flat"
                        onPress={() => openDisconnectModal(service)}
                      >
                        {t("Disconnect")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      color="primary"
                      data-testid={`connect-${service.id}`}
                      isLoading={isConnecting}
                      startContent={<Icon icon={service.icon} />}
                      onPress={() => handleConnect(service)}
                    >
                      {t("connectService", { name: service.name })}
                    </Button>
                  )}
                </div>

                {/* Connected account info */}
                {isConnected && status?.email && (
                  <div className="mt-4 pt-4 border-t border-default-200">
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Icon icon="solar:user-bold" />
                      <span>{status.email}</span>
                    </div>
                    {status.connected_at && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <Icon icon="solar:calendar-bold" />
                        <span>
                          {t("Connected on")} {new Date(status.connected_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Google Drive: Shared Drives Toggle */}
                {service.id === "google_drive" && isConnected && (
                  <div className="mt-4 pt-4 border-t border-default-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium">{t("Include Shared Drives")}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t(
                            "Search and access files from shared drives in addition to your personal drive",
                          )}
                        </p>
                      </div>
                      <Switch
                        aria-label={t("Include shared drives")}
                        data-testid="shared-drives-toggle"
                        isDisabled={isSavingSettings}
                        isSelected={supportsAllDrives}
                        size="lg"
                        onValueChange={handleSupportsAllDrivesChange}
                      />
                    </div>
                  </div>
                )}

                {/* User Search Toggle - for services that support it */}
                {service.supportsUserSearch && isConnected && (
                  <div className="mt-4 pt-4 border-t border-default-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium">{t("Use for @mentions")}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t(
                            "Include users from this service when searching for people to mention",
                          )}
                        </p>
                      </div>
                      <Switch
                        aria-label={t("Use for mentions")}
                        data-testid={`user-search-toggle-${service.id}`}
                        isDisabled={savingUserSearch === service.id}
                        isSelected={getSearchEnabledForService(service.id)}
                        size="lg"
                        onValueChange={(value) => handleUserSearchToggle(service.id, value)}
                      />
                    </div>
                  </div>
                )}

                {/* Service Credential Section - for integrations with service or both token mode */}
                {(() => {
                  const definition = integrationDefinitions[service.id];

                  return (
                    definition &&
                    (definition.token_mode === "service" || definition.token_mode === "both") && (
                      <ServiceCredentialSection
                        credentialType={definition.service_credential_type || "api_token"}
                        integrationId={service.id}
                        integrationName={service.name}
                        isAdmin={isAdmin}
                        serviceOperations={definition.service_operations || []}
                        setupGuide={definition.service_setup_guide}
                      />
                    )
                  );
                })()}
              </div>
            </React.Fragment>
          );
        })}

        {/* Microsoft 365 (OBO - auto-connected via SSO, no manual setup needed) */}
        {((integrationToggles["sharepoint"] ?? true) !== false ||
          (integrationToggles["ms_calendar"] ?? true) !== false) && (
          <>
            <Divider />
            <div className="border border-default-200 rounded-lg p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Icon className="w-10 h-10" icon="logos:microsoft-icon" />
                  <div>
                    <h3 className="font-medium">{t("Microsoft 365")}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("Connected automatically through your organization's SSO")}
                    </p>
                  </div>
                </div>
                <Chip
                  color="success"
                  size="sm"
                  startContent={<Icon icon="solar:check-circle-bold" />}
                >
                  {t("Connected via SSO")}
                </Chip>
              </div>
              <div className="mt-4 pt-4 border-t border-default-200 space-y-3">
                {(integrationToggles["sharepoint"] ?? true) !== false && (
                  <div className="flex items-center justify-between gap-3 pl-2">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" icon="mdi:microsoft-sharepoint" />
                      <span className="text-sm">{t("SharePoint")}</span>
                      <span className="text-xs text-gray-400">
                        {t("Documents & collaboration")}
                      </span>
                    </div>
                  </div>
                )}
                {(integrationToggles["ms_calendar"] ?? true) !== false && (
                  <div className="flex items-center justify-between gap-3 pl-2">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" icon="mdi:calendar" />
                      <span className="text-sm">{t("Outlook Calendar")}</span>
                      <span className="text-xs text-gray-400">{t("Events & scheduling")}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* HubSpot */}
        {(integrationToggles["hubspot"] ?? true) !== false && (
          <>
            <Divider />
            <div className="border border-default-200 rounded-lg p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Icon className="w-10 h-10" icon="fa6-brands:hubspot" />
                  <div>
                    <h3 className="font-medium">{t("HubSpot")}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("CRM, contacts, and deal management")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* External Integrations Section */}
        <Divider />
        <div className="border border-default-200 rounded-lg p-4">
          <h3 className="font-medium flex items-center gap-2 mb-2">
            <Icon className="w-5 h-5" icon="solar:server-bold" />
            <span>{t("External Integrations")}</span>
            <Chip color="default" size="sm" variant="flat">
              {t("Coming Soon")}
            </Chip>
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("Approved Integrations from official providers.")}
          </p>
        </div>

        {/* User Search Settings Section */}
        <div className="mt-8" id="mentions">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Icon className="w-5 h-5" icon="solar:user-speak-bold" />
            <span>{t("@Mention User Search")}</span>
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t(
              "Control which connected services are used when searching for people to mention in chat. Connect more services above to expand your search.",
            )}
          </p>

          {userSearchSettings && (
            <div className="space-y-3">
              {userSearchSettings.services
                .filter((service) => {
                  // Check the service's own toggle
                  if ((integrationToggles[service.service_type] ?? true) === false) return false;
                  // Microsoft 365 (service_type "microsoft") follows the ms_calendar toggle
                  if (
                    service.service_type === "microsoft" &&
                    (integrationToggles["ms_calendar"] ?? true) === false
                  )
                    return false;

                  return true;
                })
                .map((service) => (
                  <div
                    key={service.service_type}
                    className="flex items-center justify-between p-3 bg-default-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-6 h-6" icon={service.icon} />
                      <div>
                        <span className="font-medium">{service.display_name}</span>
                        {service.connected_email && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({service.connected_email})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {service.connected ? (
                        <Switch
                          aria-label={`Use ${service.display_name} for mentions`}
                          isDisabled={savingUserSearch === service.service_type}
                          isSelected={service.enabled_for_search}
                          size="sm"
                          onValueChange={(value) =>
                            handleUserSearchToggle(service.service_type, value)
                          }
                        />
                      ) : (
                        <Chip color="default" size="sm" variant="flat">
                          {t("Not connected")}
                        </Chip>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardBody>

      {/* Disconnect Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>
            {t("disconnectServiceConfirm", { name: selectedService?.name })}
          </ModalHeader>
          <ModalBody>
            <p>{t("disconnectServiceDescription", { name: selectedService?.name })}</p>
            {serviceStatuses[selectedService?.id || ""]?.email && (
              <p className="text-sm text-gray-500 mt-2">
                {t("Connected account")}: {serviceStatuses[selectedService?.id || ""]?.email}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              color="danger"
              isLoading={disconnectingService === selectedService?.id}
              onPress={handleDisconnect}
            >
              {t("Disconnect")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};
